import { useAccountsService } from '@restworld/data-services';
import { AxiosResponse } from 'axios';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import {
  ContentType as ModalContentType,
  closeEmailLoginModal,
  openEmailTokenSentModal,
  setActiveContent as setActiveModalContent,
  updateEmailVerificationDetails,
} from '../../../context/auth-flow';
import { useAnalytics } from '@restworld/utils-common';

export type UseLoginFormProps = {
  isModal?: boolean;
  currentUrl?: string;
};

export interface EmailLoginInput {
  email: string;
}

export function useEmailLoginForm({ isModal = true, currentUrl }: UseLoginFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
    setValue,
    setError,
  } = useForm<EmailLoginInput>();
  const [loading, setLoading] = useState<boolean>(false);
  const [remainingTimes, setRemainingTimes] = useState<number>(3);
  // True when the submitted email has no associated account. We never route to
  // signup anymore: the form shows an inline error + an "upload CV" CTA instead.
  const [emailNotFound, setEmailNotFound] = useState<boolean>(false);

  const updateVerificationDetails = useSetAtom(updateEmailVerificationDetails);
  const closeEmailModal = useSetAtom(closeEmailLoginModal);
  const openVerificationModal = useSetAtom(openEmailTokenSentModal);
  const setActiveContent = useSetAtom(setActiveModalContent);

  const acc = useAccountsService();
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation('auth-flow');

  const handleLogin = useCallback(
    async (data: EmailLoginInput) => {
      setLoading(true);
      setEmailNotFound(false);
      try {
        trackEvent('start_auth_flow', {
          email: data.email,
        });
        const response: AxiosResponse = await acc.passwordlessLogin(data.email, currentUrl);
        setLoading(false);
        const tokenValidityTime = response?.data?.token_validity_time;
        const waitTime = response?.data?.wait_time;
        const tv = tokenValidityTime ? parseInt(tokenValidityTime) : 0;
        const wt = waitTime ? parseInt(waitTime) : 0;
        const now = Date.now();
        updateVerificationDetails({
          waitTime: wt,
          tokenValidityTime: tv,
          email: data.email,
          tokenExpiresAt: tv ? now + tv * 1000 : undefined,
          waitExpiresAt: wt ? now + wt * 1000 : undefined,
        });
        setActiveContent(ModalContentType.EMAIL_SENT);
        setRemainingTimes((prev) => prev - 1);
        if (isModal) {
          closeEmailModal();
          openVerificationModal();
        }
      } catch (error) {
        setLoading(false);
        const statusCode = parseInt(error.code);
        // The passwordless-login endpoint returns 404 when no account exists for
        // the email. We must never open the signup modal or route to /signup
        // (that path mints legacy `lead` users). Surface an inline error on the
        // email input and let the form render the "upload CV / apply to a job"
        // CTA via the returned `emailNotFound` flag.
        if (statusCode === 404) {
          setEmailNotFound(true);
          setError('email', { message: t('email_login_email_not_found_error') });
        }
      }
    },
    [
      acc,
      closeEmailModal,
      currentUrl,
      isModal,
      openVerificationModal,
      setActiveContent,
      setError,
      t,
      trackEvent,
      updateVerificationDetails,
    ]
  );

  const onSubmit = async (data: EmailLoginInput) => {
    handleLogin(data);
  };

  return {
    loading,
    onSubmit,
    register,
    handleSubmit,
    errors,
    watch,
    control,
    setValue,
    setError,
    setLoading,
    remainingTimes,
    emailNotFound,
  };
}
